/**
 * Template de prise de besoin : Plan Directeur TI – PME V3
 * La collecte est orientee langage client / besoins d'affaires.
 * Le cadre BZ sert ensuite de grille d'analyse interne et de presentation finale.
 */

import type { IntakeTemplate } from '../templateTypes';

export const templatePdtiPmeV3: IntakeTemplate = {
  id: 'pdti-pme-v3',
  name: 'Prise de besoin Plan Directeur TI – PME V3',
  description:
    'Version orientee besoins d’affaires: contexte, objectifs, irritants, risques, processus critiques, systemes, operations, securite, budget et appetit client. Garde la page Metier et permet ensuite de mapper vers le cadre BZ 6 + 2, les tactiques, les livrables et les 3 offres de service.',
  documentTypeCode: 'PLAN_DIRECTEUR',
  version: '3.0.0',
  phases: [
    {
      id: 'phase1',
      title: 'Profil et contexte d’affaires',
      description: "Comprendre qui est le client, son contexte et sa capacite TI actuelle.",
      icon: 'Building2',
      questions: [
        {
          id: 'company_name',
          label: "Nom de l'entreprise",
          type: 'text',
          required: true,
        },
        {
          id: 'company_type',
          label: "Type d'entreprise",
          type: 'select',
          options: ['Startup / Entreprise en demarrage', 'PME etablie', 'Entreprise en croissance', 'Organisation'],
          required: true,
        },
        {
          id: 'industry',
          label: "Industrie / secteur d'activite",
          type: 'select',
          options: [
            "Cabinet d'avocats / Juridique",
            'Cabinet comptable / CPA',
            'Services professionnels',
            'Manufacturing / Production',
            'Commerce de detail',
            'Sante',
            'Finance / Assurance',
            'Technologies',
            'Construction',
            'Transport / Logistique',
            'Education',
            'Autre',
          ],
          required: true,
        },
        {
          id: 'employees',
          label: "Nombre d'employes",
          type: 'select',
          options: ['Moins de 25 employes', '25-49 employes', '50-100 employes', '100-250 employes', '250 employes et plus'],
          required: true,
        },
        {
          id: 'revenue',
          label: "Chiffre d'affaires annuel approximatif",
          type: 'select',
          options: ['Moins de 1M$', '1M$ - 3M$', '3M$ - 10M$', '10M$ - 25M$', '25M$ - 50M$', '50M$ et plus', 'A determiner'],
          required: false,
        },
        {
          id: 'it_team',
          label: 'Comment les TI sont-elles gerees aujourd’hui ?',
          type: 'select',
          options: [
            'Aucune equipe dediee',
            '1-2 personnes internes',
            '3-5 personnes internes',
            '6-10 personnes internes',
            '10+ personnes internes',
            'Principalement externalise',
            'Mode hybride interne / externe',
          ],
          required: true,
        },
        {
          id: 'it_maturity',
          label: 'Comment decririez-vous votre maturite TI actuelle ?',
          type: 'select',
          options: [
            'Reactif - on gere surtout les urgences',
            'Basique - quelques outils, peu de structure',
            'Intermediaire - systemes en place, mais optimisations requises',
            'Structure - processus et responsabilites assez clairs',
            'Strategique - les TI contribuent activement a la croissance',
          ],
          required: true,
        },
      ],
    },
    {
      id: 'phase2',
      title: 'Objectifs, ambitions et resultats attendus',
      description: 'Faire parler le client de ses objectifs avant de parler de solutions.',
      icon: 'Target',
      questions: [
        {
          id: 'main_challenge',
          label: 'Quel est le principal enjeu que vous voulez regler avec les TI ?',
          type: 'textarea',
          placeholder: 'Ex: On perd du temps, nos systemes ne se parlent pas, on manque de visibilite, la croissance est freinee...',
          required: true,
        },
        {
          id: 'business_goals',
          label: "Objectifs d'affaires pour les 12-24 prochains mois",
          type: 'multiselect',
          options: [
            'Croissance des revenus',
            "Amelioration de l'efficacite operationnelle",
            'Meilleure experience client',
            'Reduction des couts',
            'Expansion geographique',
            'Nouveaux produits ou services',
            'Transformation numerique',
            'Conformite reglementaire',
            'Reduction du risque operationnel',
            'Meilleure visibilite de gestion',
          ],
          required: true,
        },
        {
          id: 'desired_outcomes',
          label: 'A quoi ressemblerait une reussite concrete dans 6 a 12 mois ?',
          type: 'textarea',
          placeholder: 'Ex: moins de tickets repetitifs, meilleure visibilite, moins de pannes, automatisation de certains processus...',
          required: true,
        },
        {
          id: 'timeline',
          label: 'Rythme souhaite pour avancer',
          type: 'select',
          options: ['Urgent (0-3 mois)', 'Court terme (3-6 mois)', 'Progressif (6-12 mois)', 'Transformation 12-24 mois', 'Vision 3-5 ans'],
          required: true,
        },
        {
          id: 'success_metrics',
          label: 'Comment allez-vous savoir que l’accompagnement a cree de la valeur ?',
          type: 'textarea',
          placeholder: 'Ex: moins de pertes de temps, meilleur suivi, reduction des incidents, securite accrue, meilleur budget TI...',
          required: false,
        },
      ],
    },
    {
      id: 'phase3',
      title: 'Irritants, risques et contraintes',
      description: 'Identifier ce qui coute du temps, de l’argent, de la credibilite ou du risque.',
      icon: 'AlertTriangle',
      questions: [
        {
          id: 'pain_points',
          label: 'Quels sont les irritants les plus importants aujourd’hui ?',
          type: 'multiselect',
          options: [
            'Pannes ou interruptions frequentes',
            'Performance lente',
            'Support utilisateur insuffisant',
            'Processus manuels trop nombreux',
            'Manque d’integration entre systemes',
            'Manque de visibilite sur les operations',
            'Rapports difficiles a produire',
            'Couts TI difficiles a comprendre',
            'Technologie obsolete',
            'Responsabilites TI floues',
            'Risques cyber mal controles',
            'Difficulte a prioriser les projets',
          ],
          required: true,
        },
        {
          id: 'business_risks',
          label: "Quels risques vous inquietent le plus cote affaires ?",
          type: 'multiselect',
          options: [
            'Arret des operations',
            'Perte de donnees',
            'Cyberattaque / rancongiciel',
            'Non-conformite',
            'Perte de productivite',
            'Insatisfaction client',
            'Dependance a une personne cle',
            'Couts imprevus',
            'Retard de croissance',
          ],
          required: true,
        },
        {
          id: 'current_blockers',
          label: 'Qu’est-ce qui bloque ou ralentit le plus vos equipes ?',
          type: 'textarea',
          placeholder: 'Ex: doubles saisies, validations manuelles, systemes non integres, demandes TI non priorisees...',
          required: false,
        },
        {
          id: 'constraints',
          label: 'Contraintes connues',
          type: 'multiselect',
          options: [
            'Budget limite',
            'Peu de disponibilite interne',
            'Echeancier serre',
            'Contraintes de conformite',
            'Dependance a un fournisseur',
            'Resistance au changement',
            'Dette technologique',
            'Contrainte de production / heures critiques',
          ],
          required: false,
        },
      ],
    },
    {
      id: 'phase4',
      title: 'Processus critiques, systemes et donnees',
      description: 'Relier les TI aux operations reelles du client.',
      icon: 'Server',
      questions: [
        {
          id: 'critical_processes',
          label: 'Quels processus d’affaires sont les plus critiques ?',
          type: 'textarea',
          placeholder: 'Ex: ventes, production, facturation, service client, gestion de dossiers, inventaire, paie...',
          required: true,
        },
        {
          id: 'current_systems',
          label: 'Systemes et outils importants aujourd’hui',
          type: 'multiselect',
          options: [
            'ERP',
            'CRM',
            'Comptabilite / Finance',
            'Gestion de projet',
            'RH / Paie',
            'E-commerce',
            'Inventaire / Supply Chain',
            'MES / Production',
            'Business Intelligence / Analytics',
            'Microsoft 365 / Google Workspace',
            'Telephonie / communications',
            'Autre',
          ],
          required: true,
        },
        {
          id: 'infrastructure',
          label: 'Environnement TI actuel',
          type: 'multiselect',
          options: [
            'Serveurs sur site',
            'Cloud public (Azure, AWS, GCP)',
            'Cloud prive',
            'Hybride',
            'Hebergement tiers',
            'SaaS principalement',
            'Postes de travail / mobiles',
            'Reseau multi-sites',
          ],
          required: true,
        },
        {
          id: 'integration_needs',
          label: 'Ou les systemes devraient-ils mieux se parler ?',
          type: 'textarea',
          placeholder: 'Ex: CRM vers ERP, production vers inventaire, feuilles Excel vers outils officiels...',
          required: false,
        },
        {
          id: 'critical_data',
          label: 'Quelles donnees sont les plus sensibles ou importantes ?',
          type: 'textarea',
          placeholder: 'Ex: donnees clients, dossiers confidentiels, donnees financieres, proprietes intellectuelles...',
          required: false,
        },
        {
          id: 'users_impacted',
          label: "Combien d'utilisateurs seraient impactes par les ameliorations ?",
          type: 'select',
          options: ['1-10 utilisateurs', '11-25 utilisateurs', '26-50 utilisateurs', '51-100 utilisateurs', '100+ utilisateurs', 'Toute l’entreprise'],
          required: true,
        },
      ],
    },
    {
      id: 'phase5',
      title: 'Operations quotidiennes et experience utilisateur',
      description: 'Comprendre la realite terrain: support, formation, adoption et cadence.',
      icon: 'Users',
      questions: [
        {
          id: 'support_model',
          label: 'Quel type de support ou d’accompagnement vous conviendrait le mieux ?',
          type: 'select',
          options: [
            'Support reactif - intervention sur demande',
            'Support proactif - surveillance et prevention',
            'Accompagnement regulier - rencontres et suivi',
            'Direction TI fractionnelle - pilotage continu',
            'Mode hybride selon les besoins',
          ],
          required: true,
        },
        {
          id: 'service_expectations',
          label: 'Qu’attendez-vous concretement de BZ au quotidien ou au mois ?',
          type: 'textarea',
          placeholder: 'Ex: rencontres mensuelles, priorisation, suivis, support aux utilisateurs, coordination fournisseurs...',
          required: false,
        },
        {
          id: 'user_experience_issues',
          label: 'Quels irritants les utilisateurs vivent-ils le plus souvent ?',
          type: 'textarea',
          placeholder: 'Ex: lenteur, acces, mots de passe, manque de formation, processus trop complexes...',
          required: false,
        },
        {
          id: 'training_needs',
          label: 'Niveau de formation / accompagnement requis',
          type: 'select',
          options: [
            'Minimal - utilisateurs autonomes',
            'Base - quelques sessions ciblees',
            'Structure - programme de formation',
            'Continu - accompagnement dans la duree',
          ],
          required: true,
        },
      ],
    },
    {
      id: 'phase6',
      title: 'Securite, conformite et continuite',
      description: 'Couvrir les risques sans transformer la discussion en audit technique.',
      icon: 'Shield',
      questions: [
        {
          id: 'security_concerns',
          label: 'Quelles preoccupations de securite sont presentes ?',
          type: 'multiselect',
          options: [
            'Protection des donnees clients',
            'Conformite reglementaire (Loi 25, RGPD, autre)',
            'Cyberattaque / rancongiciel',
            'Acces non autorises',
            'Perte de donnees',
            'Sauvegardes / reprise apres sinistre',
            'Formation des employes a la securite',
            'Aucune preoccupation majeure identifiee',
          ],
          required: true,
        },
        {
          id: 'compliance_constraints',
          label: 'Contraintes de conformite ou obligations particulieres',
          type: 'textarea',
          placeholder: 'Ex: Loi 25, exigences clients, donnees sensibles, audit, assurance cyber...',
          required: false,
        },
        {
          id: 'continuity_expectations',
          label: 'Quel niveau d’arret ou de perte serait difficilement acceptable ?',
          type: 'textarea',
          placeholder: 'Ex: impossible d’etre arrete plus de 4 heures, donnees critiques a recuperer rapidement...',
          required: false,
        },
      ],
    },
    {
      id: 'phase7',
      title: 'Budget, appetit et choix d’accompagnement',
      description: 'Donner a BZ les bornes pour offrir un vrai choix client.',
      icon: 'Wallet',
      questions: [
        {
          id: 'budget_mensuel_bz',
          label: 'Budget mensuel estime pour l’accompagnement BZ',
          type: 'select',
          options: [
            '1500$ - Petite taille / demarrage',
            '2000$ - Petite a moyenne taille',
            '3000$ - Equilibre PME courant',
            '4000$ - PME avec besoins reguliers',
            '5000$ - PME plus structuree',
            '7500$ - Grande PME / multi-sites',
            '10000$+ - Organisation plus complexe',
            'A determiner',
          ],
          required: false,
          helpText: 'Cette base sert a generer 3 choix: essentiel, recommande et complet. Le client ne sera pas force dans une seule option.',
        },
        {
          id: 'budget_range',
          label: 'Enveloppe d’investissement TI 12-24 mois, si connue',
          type: 'select',
          options: ['Moins de 25K$', '25K$ - 50K$', '50K$ - 100K$', '100K$ - 250K$', '250K$ - 500K$', '500K$ et plus', 'A determiner'],
          required: false,
        },
        {
          id: 'budget_flexibility',
          label: 'Le budget est-il ferme ou flexible selon la valeur ?',
          type: 'select',
          options: ['Tres ferme', 'Assez ferme', 'Flexible si la valeur est claire', 'A definir avec BZ'],
          required: false,
        },
        {
          id: 'accompagnement_appetite',
          label: 'Quel appetit d’accompagnement correspond le mieux au client ?',
          type: 'select',
          options: [
            'Commencer petit et securiser les essentiels',
            'Approche equilibree avec recommandations prioritaires',
            'Transformation plus complete et proactive',
            'A determiner apres analyse',
          ],
          required: true,
        },
        {
          id: 'offer_preference',
          label: 'Si on presente 3 choix, lequel pensez-vous etre le plus naturel ?',
          type: 'select',
          options: ['Essentiel / optimise', 'Recommande / equilibre', 'Complet / transformation', 'Je veux voir les 3 avant de choisir'],
          required: false,
        },
        {
          id: 'engagement_duration',
          label: 'Duree d’engagement confortable',
          type: 'select',
          options: ['3 mois', '6 mois', '12 mois', '12 mois et plus', 'A determiner'],
          required: false,
        },
        {
          id: 'reporting_cadence',
          label: 'Cadence de suivi souhaitee',
          type: 'select',
          options: ['Hebdomadaire', 'Aux deux semaines', 'Mensuelle', 'Trimestrielle', 'Selon les priorites'],
          required: false,
        },
      ],
    },
    {
      id: 'phase8',
      title: 'Deja en place, a conserver ou a exclure',
      description: 'Eviter de vendre ou recommander ce que le client a deja, veut garder ou ne veut pas faire.',
      icon: 'CheckCircle2',
      questions: [
        {
          id: 'deja_en_place_global',
          label: 'Qu’est-ce qui est deja en place ou deja fait ?',
          type: 'textarea',
          placeholder: 'Ex: inventaire, MFA, support externe, documentation, politiques, comite TI, sauvegardes...',
          required: false,
        },
        {
          id: 'a_preserver',
          label: 'Qu’est-ce qui fonctionne bien et doit etre conserve ?',
          type: 'textarea',
          placeholder: 'Ex: fournisseur apprecie, processus interne, outil deja adopte, relation avec un partenaire...',
          required: false,
        },
        {
          id: 'a_eviter_ou_exclure',
          label: 'Y a-t-il des solutions, approches ou sujets a eviter pour l’instant ?',
          type: 'textarea',
          placeholder: 'Ex: pas de migration cloud cette annee, ne pas changer ERP, limiter les projets lourds...',
          required: false,
        },
        {
          id: 'priority_deliverables',
          label: 'Quels livrables seraient les plus utiles pour vous ?',
          type: 'multiselect',
          options: [
            'Diagnostic TI vulgarise pour la direction',
            'Roadmap 12-24 mois',
            'Budget TI macro',
            'Inventaire / cartographie des actifs',
            'Plan de securite de base',
            'Plan de continuite / reprise',
            'Tableau de bord de suivi',
            'Catalogue de services / support',
            'Plan de projets prioritaires',
            'Documentation pour operations',
          ],
          required: false,
        },
      ],
    },
  ],
};
